import { useState, useEffect } from "react";
import { DISTRIBUTION_SERVICES } from "@/data/distributionServices";
import { PLATFORM_ICON_MAP } from "@/components/episode/PlatformIcons";

const platforms = DISTRIBUTION_SERVICES.map(service => ({
  id: service.id,
  name: service.name,
  icon: PLATFORM_ICON_MAP[service.id] ?? null,
}));

const VISIBLE_MOBILE = 2;
const VISIBLE_TABLET = 3;
const VISIBLE_DESKTOP = 4;
const ROTATE_INTERVAL_MS = 2500;
const TRANSITION_MS = 300;

export function PlatformLogosCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [preloaded, setPreloaded] = useState(false);

  // Preload all platform SVGs into browser cache before starting rotation
  useEffect(() => {
    const iconUrls = platforms.map(p => p.icon).filter(Boolean) as string[];
    Promise.all(
      iconUrls.map(
        (src) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve(); // continue even if one fails
            img.src = src;
          })
      )
    ).then(() => setPreloaded(true));
  }, []);

  // Start rotation only after all icons are cached
  useEffect(() => {
    if (!preloaded) return;

    const interval = setInterval(() => {
      setIsFading(true);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % platforms.length);
        setIsFading(false);
      }, TRANSITION_MS);
    }, ROTATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [preloaded]);

  const visiblePlatforms = Array.from({ length: VISIBLE_DESKTOP }, (_, i) =>
    platforms[(currentIndex + i) % platforms.length]
  );

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3 md:gap-4 my-6 md:my-8">
      {visiblePlatforms.map((platform, index) => (
        <div
          key={platform.id}
          className={`
            flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-secondary/10 border border-secondary/20
            transition-opacity duration-300
            ${isFading ? "opacity-0" : "opacity-100"}
            ${index >= VISIBLE_MOBILE ? "hidden sm:flex" : "flex"}
            ${index >= VISIBLE_TABLET ? "sm:hidden md:flex" : ""}
          `}
          
        >
          <span className="flex-shrink-0" aria-hidden="true">
            {platform.icon ? (
              <img
                src={platform.icon}
                alt={platform.name}
                className="w-5 h-5 sm:w-6 sm:h-6 rounded"
                fetchPriority="high"
              />
            ) : (
              <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-neutral/20 flex items-center justify-center text-[10px] font-bold text-secondary/60">
                {platform.name.charAt(0)}
              </span>
            )}
          </span>
          <span className="text-xs sm:text-sm text-secondary/80 font-medium whitespace-nowrap">
            {platform.name}
          </span>
        </div>
      ))}
    </div>
  );
}
